import { Box, Button, Center, Dialog, Flex, Tabs, VStack } from '@chakra-ui/react';
import { AddPulsIcon, ButtonGray, ConfirmDialog, tools } from '@halo/ui-chat-react';
import type { Msg, SessionStoreInstance } from '@halo/chat-sdk-react';
import { isSessionCategory, SessionCategoryPrefix, useAppStore } from '@halo/chat-sdk-react';
import { useStore } from 'zustand';
import { useActionsStore } from '@/store/actions';
import { ChatsContent } from './ChatsContent';
import { ContactsContent } from './ContactsContent';
import { ForwardComposer } from './ForwardComposer';
import type { ForwardComposerInputRef } from './ForwardComposerFooter';

enum ForwardToTabs {
  Chats = 'chats',
  Contacts = 'contacts',
}

const MAX_SELECTED_COUNT = 10;

export interface ForwardToDialogProps {
  sessionStoreInstance: SessionStoreInstance;
}

export function ForwardToDialog({ sessionStoreInstance }: ForwardToDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [tabValue, setTabValue] = useState<ForwardToTabs>(ForwardToTabs.Chats);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const [selectedIds, setSelectedIds] = useState<{ chatIds: string[]; contactIds: string[] }>({
    chatIds: [],
    contactIds: [],
  });

  const [message, setMessage] = useState<Msg | undefined>(undefined);
  const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false);
  const forwardComposerRef = useRef<ForwardComposerInputRef>(null);
  const createSingleSession = useAppStore((store) => store.createSingleSession);
  const setGroupCreateIsOpen = useActionsStore((store) => store.setGroupCreateIsOpen);
  const [valid, setValid] = useState(false);

  const sessionStore = useStore(sessionStoreInstance);

  const forwardId = useMemo(() => {
    return sessionStore?.draftMessage?.forwardId;
  }, [sessionStore?.draftMessage?.forwardId]);

  const forwardMsg = useMemo(() => {
    return sessionStore?.draftMessage?.forwardMsg;
  }, [sessionStore?.draftMessage?.forwardMsg]);

  const selectedCount = useMemo(() => {
    return selectedIds.chatIds?.length || selectedIds.contactIds?.length;
  }, [selectedIds]);

  const selectChange = useCallback(
    ({ chatIds, contactIds }: { chatIds?: string[]; contactIds?: string[] }) => {
      if (chatIds?.length) {
        return setSelectedIds({ chatIds, contactIds: [] });
      }
      if (contactIds?.length) {
        return setSelectedIds({ chatIds: [], contactIds });
      }
      return setSelectedIds({ chatIds: [], contactIds: [] });
    },
    []
  );

  const resetForwardState = useCallback(() => {
    sessionStore?.setDraftMessage({
      forwardId: undefined,
      forwardMsg: undefined,
      forwardMsgUser: undefined,
    });
  }, [sessionStore]);

  const handleSend = useCallback(async () => {
    setIsLoading(true);
    try {
      const ids = [...selectedIds.chatIds, ...selectedIds.contactIds];
      const sessionIds = await Promise.all(
        ids.map(async (id) => {
          const isSingle = isSessionCategory(id, SessionCategoryPrefix.Single);
          const isGroup = isSessionCategory(id, SessionCategoryPrefix.Group);
          const isContact = !isSingle && !isGroup;
          let chatId = id;
          if (isContact) {
            const session = await createSingleSession(id);
            chatId = session?.chat_id;
          }
          return chatId;
        })
      );

      await forwardComposerRef.current?.send({
        sessionIds,
        forwardMsg,
      });

      setIsOpen(false);
      resetForwardState();
    } catch (error) {
      tools.toaster.error({
        title: (error as Error)?.message || t('tips.failedForwardMessage'),
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedIds.chatIds,
    selectedIds.contactIds,
    forwardMsg,
    resetForwardState,
    createSingleSession,
    t,
  ]);

  const checkSend = useCallback(() => {
    if (!forwardId) {
      if (valid) {
        setConfirmDialogIsOpen(true);
      } else {
        tools.toaster.error({
          id: 'forward_msg_del',
          title: t('tips.messageDeleted'),
        });
      }
      return;
    }
    handleSend();
  }, [forwardId, handleSend, valid, t]);

  useEffect(() => {
    if (forwardId) {
      setIsOpen(true);
    }
  }, [forwardId]);

  useEffect(() => {
    if (isOpen) {
      setResetTrigger(1);
      setSelectedIds({ chatIds: [], contactIds: [] });
      setTabValue(ForwardToTabs.Chats);
    }
  }, [isOpen]);

  useEffect(() => {
    if (forwardMsg) {
      setMessage(forwardMsg);
    }
  }, [forwardMsg]);

  return (
    <>
      <Dialog.Root
        placement="center"
        open={isOpen}
        onOpenChange={(details) => setIsOpen(details.open)}
        lazyMount
        unmountOnExit
        restoreFocus={false}
        trapFocus={false}
        closeOnInteractOutside={false}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            pt="2.5"
            px="5"
            pb="5"
            w="560px"
            maxW="100%"
            bg="bg.subtle"
            maxH="calc(100vh - 48px)"
          >
            <Dialog.Header
              px="0"
              py="2.5"
              fontSize="md"
              fontWeight="semibold"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>{t('chat.forwardTitle')}</Box>
              <Box
                display="flex"
                alignItems="center"
                gap="1"
                px="3"
                cursor="pointer"
                fontWeight="medium"
                fontSize="sm"
                onClick={() => {
                  setIsOpen(false);
                  setGroupCreateIsOpen(true);
                }}
              >
                <Center p="1" cursor="pointer" color="fg" fontSize="lg">
                  <AddPulsIcon />
                </Center>
                {t('action.createGroupAndForward')}
              </Box>
            </Dialog.Header>
            <Dialog.Body p="0" py="2" overflow="hidden" display="flex">
              <VStack gap="0" w="full">
                <Tabs.Root
                  size="sm"
                  flex="1"
                  overflow="hidden"
                  w="full"
                  display="flex"
                  flexDirection="column"
                  value={tabValue}
                  onValueChange={(details) => {
                    setTabValue(details.value as ForwardToTabs);
                    setSelectedIds({ chatIds: [], contactIds: [] });
                    setResetTrigger((prev) => prev + 1);
                  }}
                >
                  <Tabs.List pos="relative" gap="6" px="0">
                    <Tabs.Trigger
                      _before={{ w: '3', margin: 'auto', background: 'fg' }}
                      px="0"
                      value={ForwardToTabs.Chats}
                    >
                      {t('chat.chats')}
                    </Tabs.Trigger>
                    <Tabs.Trigger
                      _before={{ w: '3', margin: 'auto', background: 'fg' }}
                      px="0"
                      value={ForwardToTabs.Contacts}
                    >
                      {t('nav.contacts')}
                    </Tabs.Trigger>
                  </Tabs.List>
                  <Tabs.Content value={ForwardToTabs.Chats} overflow="auto">
                    <ChatsContent
                      resetTrigger={resetTrigger}
                      maxSelectedCount={MAX_SELECTED_COUNT}
                      selectChange={selectChange}
                    />
                  </Tabs.Content>
                  <Tabs.Content value={ForwardToTabs.Contacts} overflow="auto">
                    <ContactsContent
                      resetTrigger={resetTrigger}
                      maxSelectedCount={MAX_SELECTED_COUNT}
                      selectChange={selectChange}
                    />
                  </Tabs.Content>
                </Tabs.Root>
                <ForwardComposer
                  message={message}
                  refComponent={forwardComposerRef}
                  onValidChange={setValid}
                />
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Flex className="ForwardToDialogFooter" justifyContent="flex-end">
                <ButtonGray
                  onClick={() => {
                    setIsOpen(false);
                    resetForwardState();
                  }}
                >
                  {t('buttons.cancel')}
                </ButtonGray>
                <Button
                  disabled={selectedCount === 0}
                  gap="1"
                  rounded="xl"
                  minW="96px"
                  onClick={checkSend}
                  loading={isLoading}
                >
                  {selectedCount > 0 && <Box as="span">{selectedCount}</Box>}
                  <Box as="span">{t('buttons.send')}</Box>
                </Button>
              </Flex>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
      <ConfirmDialog
        open={confirmDialogIsOpen}
        onClose={() => setConfirmDialogIsOpen(false)}
        title={t('chat.messageDeleted')}
        subTitle={t('chat.messageDeletedSubTitle')}
        onOk={async () => {
          await handleSend();
          setConfirmDialogIsOpen(false);
        }}
        onCancel={() => {
          setConfirmDialogIsOpen(false);
          setIsOpen(false);
          resetForwardState();
        }}
        cancelText={t('buttons.cancel')}
        okText={t('buttons.confirm')}
        okButtonProps={{
          loading: isLoading,
        }}
      />
    </>
  );
}
